import SkuCardMobile from '@/components/common/cards/sku/mobile/SkuCardMobile';
import LoadingComponent from '@/components/common/LoadingComponent';
import LoadingImage from '@/components/common/LoadingImage';
import SkuDescription from '@/components/skuDescription';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import useWishlist from '@/hooks/useWishlist';
import { SkusMobileAlgoliaHitsProps } from '@/types/props/components/skus';
import { SkuDTO } from '@/types/api/sku';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { useFetchMultipleSkus } from '@/useQuery/queries/skus';
import { getSkuMainImage } from '@/utilites/get-sku-main-image';
import { getTrendingPercent, toDe } from '@/utilites/sku-operations';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';

export default function SkusMobileAlgoliaHits({
  isLowestAskSortBySelected,
  viewMode,
}: SkusMobileAlgoliaHitsProps) {
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { isInWishlist } = useWishlist();
  const { items } = useHits<SkuDTO>();
  const [skus, setSkus] = useState<SkuDTO[] | null>(Array(21).fill(null));
  const multipleSkusQuery = useFetchMultipleSkus(items.map(item => item.slug));

  useEffect(() => {
    const algoliaItems = items.map(item => {
      const parsedName = JSON.parse(JSON.stringify({ name: item.name })).name;
      const cleanName = parsedName.replace(/\\"/g, '"').replace(/\\{2}/g, '');

      return {
        ...item,
        name: cleanName,
        isInWishlist: isInWishlist(item.slug) ?? false,
        trendingPercent: 0,
        mainImageUrl: getSkuMainImage(item.mainImageUrl),
      };
    });

    if (multipleSkusQuery.isLoading || multipleSkusQuery.isFetching) {
      setSkus(Array(21).fill(null));
      return;
    }

    if (!multipleSkusQuery.isLoading && multipleSkusQuery.data) {
      const backendSkusFormatted = multipleSkusQuery.data.map(sku => ({
        ...sku,
        __position: algoliaItems.find(item => item.slug === sku.slug)?.__position || 999999,
        isInWishlist: isInWishlist(sku.slug) ?? false,
        trendingPercent: sku?.orders
          ? getTrendingPercent({
              orders: sku.orders,
              retailPrice: sku.retailPrice,
            })
          : 0,
        mainImageUrl: getSkuMainImage(sku.mainImageUrl),
      }));

      const skusInBackend = multipleSkusQuery.data.map(sku => sku.slug);
      const skusNotInBackend = algoliaItems.filter(item => !skusInBackend.includes(item.slug));

      const skusMix = [...backendSkusFormatted, ...skusNotInBackend];

      if (isLowestAskSortBySelected) {
        const sortedSkusMix = skusMix.sort(
          (a, b) => (a.lowestAsk || 0) - (b.lowestAsk || 0)
        ) as SkuDTO[];
        setSkus(sortedSkusMix);
      } else {
        setSkus(skusMix.sort((a, b) => (a.__position || 0) - (b.__position || 0)) as SkuDTO[]);
      }

      return;
    }

    if (multipleSkusQuery.isError && algoliaItems.length > 0) {
      setSkus(algoliaItems);
      return;
    }

    if (algoliaItems.length === 0) {
      setSkus(null);
    }
  }, [
    multipleSkusQuery.isError,
    multipleSkusQuery.isLoading,
    multipleSkusQuery.data,
    isLowestAskSortBySelected,
    items,
    isInWishlist,
    multipleSkusQuery.isFetching,
  ]);

  if (viewMode === 'grid') {
    return (
      <ul className="tw-grid tw-grid-cols-2 tw-gap-3 tw-p-0">
        {skus === null ? (
          <span>{translate(CommonEnum['no_products_found'])}</span>
        ) : (
          skus.map((sku, index) => (
            <li key={sku?.id || index} className="tw-list-none">
              <SkuCardMobile sku={sku} />
            </li>
          ))
        )}
      </ul>
    );
  }

  return (
    <ul className="gap-4 tw-flex tw-flex-col tw-p-0">
      {skus === null ? (
        <span>{translate(CommonEnum['no_products_found'])}</span>
      ) : (
        <>
          <li className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-cool-gray tw-pb-3 tw-font-plusJakartaSans tw-text-xs tw-font-semibold tw-text-cool-gray">
            <span>{translate(CommonEnum.name).toUpperCase()}</span>
            <span>{translate(CommonEnum.action).toUpperCase()}</span>
          </li>

          {skus.map((sku, index) => {
            if (sku) {
              return (
                <li
                  key={sku.id || index}
                  className="tw-flex tw-items-center tw-justify-between tw-gap-5"
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div>
                      <Image
                        src={sku.mainImageUrl || ''}
                        alt={sku.name || ''}
                        width={74}
                        height={74}
                        className="tw-rounded-lg tw-border tw-border-solid tw-border-platinum-gray tw-object-contain tw-p-1"
                      />
                    </div>
                    <div>
                      <p className="tw-flex tw-flex-col tw-gap-1 tw-p-0 tw-font-plusJakartaSans tw-text-xs tw-font-medium tw-text-slate-gray">
                        <strong className="tw-line-clamp-1 tw-font-plusJakartaSans tw-text-sm tw-font-semibold tw-text-textBlack">
                          {sku.name}
                        </strong>
                        <SkuDescription
                          skuBottleSizeInMl={sku.bottleSizeInMl || null}
                          skuVolumeInPercent={sku.volumeInPercent || null}
                          skuReleaseDate={sku.releaseDate || null}
                        />
                      </p>
                    </div>
                  </div>
                  <div className="tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-bg-[#15253F1A] tw-p-2 tw-px-[20px] tw-py-[10px] tw-text-center tw-font-plusJakartaSans tw-text-xs-custom tw-font-semibold tw-text-text">
                    <span>{translateCommon(CommonEnum['ask'])}</span>
                    <span>
                      {sku.lowestAsk && sku.lowestAsk > 0
                        ? toDe(sku.lowestAsk || 0, {
                            withDigits: false,
                            withSymbol: true,
                          })
                        : '--'}
                    </span>
                  </div>
                </li>
              );
            }

            return (
              <LoadingComponent
                as="div"
                key={index}
                componentProps={{
                  className: 'tw-flex tw-items-center tw-gap-3 tw-justify-between',
                }}
                loading={true}
              >
                <div className="tw-flex tw-items-center tw-gap-3">
                  <div>
                    <LoadingImage className="tw-h-[74px] tw-w-[74px] tw-rounded-lg" loading={true}>
                      <></>
                    </LoadingImage>
                  </div>
                  <div>
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-p-0 tw-font-plusJakartaSans tw-text-xs tw-font-medium tw-text-slate-gray">
                      <strong className="tw-line-clamp-1 tw-font-plusJakartaSans tw-text-sm tw-font-semibold tw-text-textBlack">
                        Macallan 30 Years ...
                      </strong>
                      <span>70 cl. | 2018</span>
                    </div>
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-bg-[#15253F1A] tw-p-2 tw-text-center tw-font-plusJakartaSans tw-text-xs-custom tw-font-semibold tw-text-text">
                  <span>Ask</span>
                  <span>100 €</span>
                </div>
              </LoadingComponent>
            );
          })}
        </>
      )}
    </ul>
  );
}
